import { ExtractErrorInfo } from "@components/shared/ExtractErrorInfo";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api } from "Config/Api";
import { API_ENDPOINTS } from "Config/apiConfig";
import { RootState } from "store/store";
import { AppType } from "types/appType";
import { ErrorType } from "types/errorType";
import { IncidentJobsheetType } from "types/incidentJobsheetType";
import { JobsheetType } from "types/jobsheetType";
import extractIdFromLocation from "utils/extractIdFromLocation";

export interface AppTypeWithIncidentCreateType extends AppType {
  loading: boolean;
  error: ErrorType | null;
  jobsheets: JobsheetType[];
  incidentJobsheets: JobsheetType[];
}

const initialState: AppTypeWithIncidentCreateType = {
  loading: false,
  error: null,
  jobsheets: [],
  incidentJobsheets: [],
};

// Async thunk to fetch jobsheets by client asset reference
export const fetchJobsheets = createAsyncThunk<JobsheetType[], string>(
  "jobsheets/fetchJobsheets",
  async (clientAssetId, { rejectWithValue }) => {
    try {
      const response = await api.get<JobsheetType[]>(API_ENDPOINTS.jobsheets, {
        params: { ClientAssetId: clientAssetId },
      });

      const data = response.data as unknown;
      if (Array.isArray(data)) {
        return data;
      }

      const results = (data as { results?: JobsheetType[] }).results;
      return Array.isArray(results) ? results : [];
    } catch (error: any) {
      const errorInfo = ExtractErrorInfo(error);
      return rejectWithValue(errorInfo);
    }
  }
);

// Async thunk to fetch jobsheets linked to an incident
export const fetchIncidentJobsheets = createAsyncThunk<JobsheetType[], string>(
  "jobsheets/fetchIncidentJobsheets",
  async (incidentId, { rejectWithValue }) => {
    try {
      const response = await api.get<JobsheetType[]>(API_ENDPOINTS.incidentJobsheets(incidentId));

      const data = response.data as unknown;
      if (Array.isArray(data)) {
        return data;
      }

      const results = (data as { results?: JobsheetType[] }).results;
      return Array.isArray(results) ? results : [];
    } catch (error: any) {
      const errorInfo = ExtractErrorInfo(error);
      return rejectWithValue(errorInfo);
    }
  }
);

// Add a jobsheet to an incident in edit mode - linking an existing jobsheet to an incident
export const addIncidentJobsheet = createAsyncThunk<
  JobsheetType,
  { incidentId: string; jobsheet: IncidentJobsheetType }
>("jobsheets/addIncidentJobsheet", async ({ incidentId, jobsheet }, { rejectWithValue }) => {
  try {
    const response = await api.post<JobsheetType>(API_ENDPOINTS.addIncidentJobsheet(incidentId), jobsheet);
    const locationHeader = response.headers["location"] || response.headers["Location"];
    if (locationHeader && !response.data?.id) {
      return {
        ...response.data,
        id: extractIdFromLocation(locationHeader),
      } as JobsheetType;
    }

    return response.data;
  } catch (error: any) {
    return rejectWithValue(ExtractErrorInfo(error));
  }
});

// Delete a jobsheet from an incident in edit mode - unlinking a jobsheet from an incident
export const deleteJobsheet = createAsyncThunk<string, { incidentId: string; jobsheetId: string }>(
  "jobsheets/deleteJobsheet",
  async ({ incidentId, jobsheetId }, { rejectWithValue }) => {
    try {
      await api.delete(API_ENDPOINTS.deleteIncidentJobsheet(incidentId, jobsheetId));
      return jobsheetId;
    } catch (error: any) {
      return rejectWithValue(ExtractErrorInfo(error));
    }
  }
);

const jobsheetSlice = createSlice({
  name: "jobsheet",
  initialState,
  reducers: {
    clearJobsheetError: (state) => {
      state.error = null;
    },
    clearJobsheets: (state) => {
      state.jobsheets = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchJobsheets.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchJobsheets.fulfilled, (state, action) => {
      state.loading = false;
      state.jobsheets = action.payload;
    });
    builder.addCase(fetchJobsheets.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorType;
    });
    builder.addCase(fetchIncidentJobsheets.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchIncidentJobsheets.fulfilled, (state, action) => {
      state.loading = false;
      state.incidentJobsheets = action.payload;
    });
    builder.addCase(fetchIncidentJobsheets.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorType;
    });
    builder.addCase(addIncidentJobsheet.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(addIncidentJobsheet.fulfilled, (state, action) => {
      state.loading = false;
      state.incidentJobsheets = [...state.incidentJobsheets, action.payload];
    });
    builder.addCase(addIncidentJobsheet.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorType;
    });
    builder.addCase(deleteJobsheet.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteJobsheet.fulfilled, (state, action) => {
      state.loading = false;
      state.incidentJobsheets = state.incidentJobsheets.filter((jobsheet) => jobsheet.id !== action.payload);
    });
    builder.addCase(deleteJobsheet.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorType;
    });
  },
});

export const jobsheetsRootState = (state: RootState) => state.jobsheets;
export const { clearJobsheetError, clearJobsheets } = jobsheetSlice.actions;

export default jobsheetSlice.reducer;
